import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Tab,
  Button,
  Tabs,
  Typography,
  BottomNavigation,
} from "@mui/material";
import {
  HomesiteColDef,
  TableColumnMap,
  useHomesite,
} from "../../../../../../components/core/homesites/homesiteColumns";
import AdditionalHomeSiteDetailsForm from "./AdditionalHomeSiteDetailsForm";
import { IPatchRequest } from "../../../../../../api/common/patch";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { IHomesite } from "../../../../../../api/homesites/homesites.types";
import { useDialogBoxContext } from "../../../../../../components/decisionDialogBox/decisionDialogBoxContext";
import { formatDate, formatDateTime, useBrowserClosureWarning } from "../../../../../../utils/functions";

import {
  decisionDialogBoxMessage,
  decisionDialogBoxTitle,
} from "../../../../../../components/decisionDialogBox/decisionDialogBox";
import { GlobalSnackNotify } from "../../../../../../components/globalSnackNotify/globalSnackNotify";
import { useUpdateHomeSite } from "../../../../../../reactQuery/homeSites/homsites/homeSiteQuery";
import {normaliseDate, normalizeDateTime, ValidatorResponse } from "../../../../../../utils/common/validator";
import dayjs from "dayjs";
import { NHC_DATE_TIME, NHCDATE, NHCTIME } from "../../../../../../components/core/homesites/homesiteColumnConsts";
import { buildNHCDateTimePatch } from "../../../../../../utils/common/buildNHCDateTimePatch";

function isFutureFromToday(value: unknown): boolean {
  if (value == null || value === "") return false;
  let d: Date;
  if (value instanceof Date) {
    d = value;
  } else {
    const str = String(value);
    d = /^\d{4}-\d{2}-\d{2}$/.test(str) ? new Date(str + "T12:00:00") : new Date(str);
  }
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dCopy = new Date(d.getTime());
  dCopy.setHours(0, 0, 0, 0);
  return dCopy.getTime() > today.getTime();
}

interface AdditionalDetailsProps {
  homesite: IHomesite;
  divisionCode: string;
  conciergeEmail: string;
  updateHomesite: (data: any) => void;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
  setChildHandlers?: (handlers: {
    save: () => Promise<void>;
    cancel: () => void;
  }) => void;
}

export type EditedValues = {
  value: any;
  isValid: boolean;
  valueValidator?: (value: any) => ValidatorResponse;
  valueGetter?: (value: any, row: any) => any;
};

const AdditionalDetails: React.FC<AdditionalDetailsProps> = ({
  homesite,
  updateHomesite,
  divisionCode,
  conciergeEmail,
  onUnsavedChangesChange,
  setChildHandlers,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [editedFields, setEditedFields] = useState<
    Record<string, EditedValues>
  >({}); // Add a type to standartize
  const { setBuyerlistingTables } = useHomesite();
  const { showDecisionDialogBox } = useDialogBoxContext();
  const isTryingToLeave = Object.keys(editedFields).length > 0;
  const updateHomesiteMutation = useUpdateHomeSite(divisionCode);
  useBrowserClosureWarning(isTryingToLeave);

  /* Update onUnsavedChangesChange when isTryingToLeave changes */
  useEffect(() => {
    onUnsavedChangesChange?.(isTryingToLeave);
  }, [isTryingToLeave, onUnsavedChangesChange]);
  // UseMemo for optimizing column fetching
  const buyerColumns = useMemo(
    () =>
      setBuyerlistingTables(TableColumnMap.BuyerListingBuyerFields).filter(
        (column) => !["actions", "numExceptions", "numAlerts", "numNotes", "contactInfo", "milestoneProgress"].includes(column.field)
      ),
    [setBuyerlistingTables]
  );

  const homesiteColumns = useMemo(
    () =>
      setBuyerlistingTables(TableColumnMap.BuyerListingHomeSiteFields).filter(
        (column) => !["actions", "numExceptions", "numAlerts", "numNotes", "contactInfo", "milestoneProgress"].includes(column.field)
      ),
    [setBuyerlistingTables]
  );

  const mortgageTitleColumns = useMemo(
    () =>
      setBuyerlistingTables(TableColumnMap.BuyerListingMortgageTitleFields).filter(
        (column) => !["actions", "numExceptions", "numAlerts", "numNotes", "contactInfo", "milestoneProgress"].includes(column.field)
      ),
    [setBuyerlistingTables]
  );

  const oslColumns = useMemo(
    () =>
      setBuyerlistingTables(TableColumnMap.BuyerListingOSLFields).filter(
        (column) => !["actions", "numExceptions", "numAlerts", "numNotes", "contactInfo", "milestoneProgress"].includes(column.field)
      ),
    [setBuyerlistingTables]
  );

  const handleFieldChange = (field: HomesiteColDef, newValue: any) => {
    let originalValue = field.valueGetter
      ? field.valueGetter(null, homesite)
      : "";

    let valuesAreIdentical = false;

    if (field.type === "date") {
      valuesAreIdentical = normaliseDate(newValue) === normaliseDate(formatDate(originalValue));
    } else if (field.type === "dateTime") {
      valuesAreIdentical = normalizeDateTime(newValue) === normalizeDateTime(formatDateTime(originalValue));
    } else {
      const normalizedNew = newValue ?? "";
      const normalizedOriginal = originalValue ?? "";
      valuesAreIdentical = normalizedNew === normalizedOriginal;
    }
    if (valuesAreIdentical) {
      setEditedFields((prev) => {
        const updated = { ...prev };
        delete updated[field.field];
        return updated;
      });
    } else {
      setEditedFields((prev) => {
        const updated = { ...prev };
        // const f = updated.get(field);
        updated[field.field] = {
          value: newValue,
          isValid: field.valueValidator
            ? field.valueValidator(newValue).isValid
            : true,
          valueValidator: field.valueValidator,
          valueGetter: field.valueGetter,
        };
        return updated;
      });
    }
  };

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info",
  });

  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "info"
  ) => {
    setNotification({ open: true, message, severity: severity });
  };

  const changesAreValid = (editedFields: Record<string, any>): boolean => {
    for (const key in editedFields) {
      const value = editedFields[key];
      if (value.valueValidator) {
        const validationResult = value.valueValidator(value.value);
        if (!validationResult.isValid) {
          return false;
        }
      }
    }
    return true;
  };

  const handleSave = useCallback(async () => {
    if (Object.keys(editedFields).length > 0 && changesAreValid(editedFields)) {
      const patchRequest: IPatchRequest = {
        patches: Object.keys(editedFields)
          .flatMap((key) => {
            const evalue = editedFields[key];
            const fromValue = evalue.valueGetter
              ? evalue.valueGetter(null, homesite)
              : null;
            const toValue = evalue.value;

            if (key === NHC_DATE_TIME) {
              const v = toValue ? dayjs(toValue) : null;
            
              return buildNHCDateTimePatch(
                homesite.NHCDate ?? null,   
                homesite.NHCTime ?? null,
                v                        
              ).patches;
            } 
            else {
              return [
                {
                  from: fromValue,
                  to: toValue,
                  op: "set",
                  property: `/${key}`,
                },
              ];
            }
          })
      };

      try {
        await updateHomesiteMutation.mutateAsync({
          divisionCode: divisionCode,
          homesiteId: homesite.id,
          body: patchRequest,
        });
        // Updating homesite with new values from editedFields to 
        // reflect new changes are we are clearing editedValues post save

        Object.keys(editedFields).forEach((key) => {
          if (key === NHC_DATE_TIME) {
            const [datePart = "", timePart = ""] = ((editedFields[key].value as string) ?? "").split(" ");
            homesite[NHCDATE] = datePart ? dayjs(datePart, "MM-DD-YYYY").format("YYYY-MM-DD") : "";
            homesite[NHCTIME] = timePart ? dayjs(timePart, "HH:mm").format("HH:mm:ss") : "";
          } else {
            (homesite as any)[key] = editedFields[key].value;
          }
    });
        showSnackbar("Changes saved successfully!", "success");
      } catch (error) {
        showSnackbar("Failed to save changes", "error");
      }
      setEditedFields({});
    }
  }, [divisionCode, editedFields, homesite]);

  const handleCancel = useCallback(() => {
    setEditedFields({});
  }, []);

  /* Update onUnsavedChangesChange when isTryingToLeave changes */
  useEffect(() => {
    onUnsavedChangesChange?.(isTryingToLeave);
  }, [isTryingToLeave, onUnsavedChangesChange]);

  /* Set child handlers for save and cancel actions */
  useEffect(() => {
    setChildHandlers?.({
      save: handleSave,
      cancel: handleCancel,
    });
  }, [setChildHandlers, handleSave, handleCancel]);

  const renderField = (fields: HomesiteColDef[]) => {
    return (
      <AdditionalHomeSiteDetailsForm
        key={fields[0].field}
        fields={fields}
        editedFields={editedFields}
        homesite={homesite}
        onChange={handleFieldChange}
      />
    );
  };

  const handleTabChangeWithCheck = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    if (isTryingToLeave) {
      showDecisionDialogBox(
        true,
        decisionDialogBoxTitle,
        decisionDialogBoxMessage,
        () => {
          handleSave();
          setTabValue(newValue);
        },
        () => {
          handleCancel();
          setTabValue(newValue);
        }
      );
    } else {
      setTabValue(newValue);
      setEditedFields({});
    }
  };

  return (
    <>
      <Box
        sx={{
          width: "1020px",
          height: "564px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ paddingBottom: "4px" }}>
          <Box sx={{ marginRight: 3, marginBottom: 1, height: "10px" }}>
            <Typography
              variant="caption"
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                color: "primary.main",
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 15, marginRight: 1 }} />
              Editable fields
            </Typography>
          </Box>
          <Box sx={{ width: "1020px", height: "48px", marginBottom: "8px" }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChangeWithCheck}
              sx={tabsStyles}
            >
              <Tab label="Buyer Details" value={0} />
              <Tab label="Homesite Details" value={1} />
              <Tab label="Mortgage and Title Details" value={2} />
              <Tab label="OSL Details" value={3} />
            </Tabs>
          </Box>
        </Box>
        <Box
          sx={{
            width: "1020px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          {tabValue === 0 && renderField(buyerColumns)}
          {tabValue === 1 && renderField(homesiteColumns)}
          {tabValue === 2 && renderField(mortgageTitleColumns)}
          {tabValue === 3 && renderField(oslColumns)}
        </Box>
      </Box>
      <Box
        sx={{
          width: "1020px",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginBottom: "50px",
        }}
      >
        <BottomNavigation
          style={{
            position: "absolute",
            bottom: 0,
            alignItems: "center",
            justifyContent: "flex-end",
            backgroundColor: "white",
            width: "1020px",
          }}
        >
          <GlobalSnackNotify
            open={notification.open}
            onClose={() => setNotification({ ...notification, open: false })}
            message={notification.message}
            severity={notification.severity}
            style={{
              position: "relative",
              transform: "translateX(-600px) translateY(15px)",
            }}
          />
          <Button
            variant="outlined"
            sx={{
              border: "2px solid #005DAA",
              textTransform: "none",
              ":hover": {
                border: "2px solid #005DAA",
                boxShadow: "none",
              },
            }}
            onClick={handleCancel}
            disabled={Object.keys(editedFields).length === 0}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              marginLeft: "8px",
              marginRight: "20px",
            }}
            onClick={handleSave}
            disabled={(() => {
              if (!(Object.keys(editedFields).length > 0 && changesAreValid(editedFields))) {
                return true; // No changes or invalid changes
              }
              // Block save when revisedEcoe is a future date and no delayed reason is set
              if ("revisedEcoe" in editedFields && isFutureFromToday(editedFields["revisedEcoe"]?.value)) {
                const delayedReason =
                  editedFields["delayedReason"]?.value ?? homesite.delayedReason ?? "";
                if (!delayedReason) return true;
              }
              return false;
            })()}
          >
            Save
          </Button>
        </BottomNavigation>
      </Box>
    </>
  );
};

const tabsStyles = {
  borderBottom: 1,
  borderColor: "transparent",
  "& .MuiTabs-flexContainer": {
    justifyContent: "flex-start",
    gap: 18,
    flexWrap: "nowrap",
  },
  "& .MuiTab-root": {
    fontSize: "16px",
    textTransform: "none",
    padding: "12px 8px",
    margin: 0,
    whiteSpace: "nowrap",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "#005DAA",
  },
};

export default AdditionalDetails;
